import { Component, inject, Input, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { AssignComplianceAssetsRequest, Compliance } from '../../../models/compliances.model';
import { CompliancesLibraryService } from '../../../services/compliances-library.service';
import { AccountService } from '../../../../../../_shared/services/account.service';
import { ToastrService } from 'ngx-toastr';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { UnifiedTreeItem } from '../../../../../../_shared/models/driver.model';
import { buildAssetTree } from '../../../../../../_shared/helpers/hierarchy.helper';
import { AssetSelection, ComplianceAssetPickerComponent } from './compliance-asset-picker/compliance-asset-picker.component';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-compliance-assign-modal',
  standalone: true,
  imports: [CommonModule, ScrollingModule, ComplianceAssetPickerComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './compliance-assign-modal.component.html',
  styleUrl: './compliance-assign-modal.component.scss'
})
export class ComplianceAssignModalComponent implements OnInit, OnDestroy {
  @Input() compliance!: Compliance;
  @Input() accountIds: number[] = [];

  private activeModal = inject(NgbActiveModal);
  private complianceService = inject(CompliancesLibraryService);
  private accountService = inject(AccountService);
  private toastr = inject(ToastrService);
  private errorHandler = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);
  private cancelSubject = new Subject<void>();

  assignedAssetIds: number[] = [];
  assetsTree: UnifiedTreeItem[] = [];
  assetsLoaded: WritableSignal<boolean> = signal(false);
  selectedAssets: AssetSelection[] = [];

  isAssigning = false;

  groupedAssets: { accountName: string; items: AssetSelection[]; collapsed: boolean }[] = [];
  flatAssetsModalList: any[] = [];

  get isAssignDisabled(): boolean {
    return this.isAssigning || this.selectedAssets.length === 0;
  }

  ngOnInit(): void {
    this.loadAssetsAsync();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private getScopedAccountIds(complianceAccountIds: number[]): number[] {
    if (this.accountIds.length === 0) {
      return complianceAccountIds;
    }

    const selectedAccountIds = new Set(this.accountIds);
    return complianceAccountIds.filter(accountId => selectedAccountIds.has(accountId));
  }

  private async loadAssetsAsync(): Promise<void> {
    try {
      await this.loadAssignedAssetIdsAsync();

      const accountIdsResponse = await this.complianceService.getComplianceAccountIdsAsync(
        this.compliance.id, this.cancelSubject
      );
      const complianceAccountIds = accountIdsResponse.data ?? [];
      const scopedAccountIds = this.getScopedAccountIds(complianceAccountIds);

      if (scopedAccountIds.length === 0) {
        this.assetsTree = [];
        this.assetsLoaded.set(true);
        return;
      }

      const assetsResponse = await this.accountService.getUserAccountAssets(
        scopedAccountIds, this.cancelSubject
      );

      if (assetsResponse.data) {
        this.assetsTree = buildAssetTree(assetsResponse.data);
      }
      this.assetsLoaded.set(true);
    } catch (error: any) {
      this.errorHandler.handleError(error, {
        callback: () => {
          this.assetsLoaded.set(true);
        }
      });
    }
  }

  private async loadAssignedAssetIdsAsync(): Promise<void> {
    const response = await this.complianceService.getComplianceAssignedAssetsAsync(
      this.compliance.id,
      this.cancelSubject
    );

    this.assignedAssetIds = (response.data ?? []).map(asset => asset.assetId);
  }

  onSelectionChanged(assets: AssetSelection[]): void {
    this.selectedAssets = assets;
    const groups: { [accountName: string]: AssetSelection[] } = {};
    for (const asset of assets) {
      const account = asset.accountName || '';
      if (!groups[account]) groups[account] = [];
      groups[account].push(asset);
    }
    this.groupedAssets = Object.entries(groups).map(([accountName, items]) => ({
      accountName,
      items,
      collapsed: false
    }));
    this.rebuildFlatList();
  }

  toggleCollapse(accountName: string): void {
    const group = this.groupedAssets.find(g => g.accountName === accountName);
    if (group) {
      group.collapsed = !group.collapsed;
      this.rebuildFlatList();
    }
  }

  private rebuildFlatList(): void {
    const flat: any[] = [];
    for (const group of this.groupedAssets) {
      flat.push({ rowType: 'group', accountName: group.accountName, count: group.items.length, collapsed: group.collapsed });
      if (!group.collapsed) {
        for (const item of group.items) {
          flat.push({ rowType: 'item', name: item.name, accountName: group.accountName });
        }
      }
    }
    this.flatAssetsModalList = flat;
  }

  async assignAsync(): Promise<void> {
    if (this.isAssignDisabled) return;
    this.isAssigning = true;
    try {
      const request: AssignComplianceAssetsRequest = {
        complianceId: this.compliance.id,
        assetIds: this.selectedAssets.map(a => a.id)
      };
      await this.complianceService.assignAssetsAsync(request);
      this.toastr.success(this.transloco.translate('compliance.library.assignModal.toasts.assigned'));
      this.isAssigning = false;
      this.activeModal.close(true);
    } catch (error: any) {
      this.errorHandler.handleError(error, {
        callback: () => {
          this.isAssigning = false;
        }
      });
    }
  }

  close(): void {
    this.activeModal.dismiss();
  }
}